import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import QrScannerModal from "../../components/QrScannerModal";
import {
    QrCode, MapPin, User, Truck, CheckCircle, WarningCircle, Clock, SignOut,
    CircleNotch, ArrowLeft, MagnifyingGlass,
} from "@phosphor-icons/react";

const inputCls = "w-full h-9 px-3 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none bg-white";

function elapsed(clockIn) {
    const ms = Date.now() - new Date(clockIn).getTime();
    const mins = Math.max(0, Math.floor(ms / 60000));
    const h = Math.floor(mins / 60), m = mins % 60;
    return `${h}h ${m}m`;
}

/* HTML <input type="datetime-local"> works in local time with no offset —
 * format a Date into that shape, and convert back to a real UTC ISO string
 * via `new Date(value).toISOString()` (browsers interpret an offset-less
 * datetime-local string as local time, per spec). */
function toLocalDatetimeValue(d) {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* QR codes printed on posters/badges encode a full deep-link URL (so a plain
 * phone camera can open them directly) — e.g. ".../clock-in?code=BR-SITE:xyz".
 * Our own in-app scanner decodes that same string, so pull the raw code back
 * out of it; plain "BR-EMP:"/"BR-SITE:" text (manual entry, older prints) is
 * passed through unchanged. */
function extractCode(text) {
    try {
        const url = new URL(text);
        const c = url.searchParams.get("code");
        if (c) return c;
    } catch { /* not a URL — treat as a raw code */ }
    return text;
}

const LS_PROJECT_KEY = "rom_clockin_last_project";

export default function QrClockIn({ employees, projects, initialCode }) {
    const [project, setProject] = useState(() => {
        try { return JSON.parse(localStorage.getItem(LS_PROJECT_KEY) || "null"); } catch { return null; }
    });
    const [personType, setPersonType] = useState(null); // employee | subcontractor
    const [resolvedPerson, setResolvedPerson] = useState(null); // {type, id, name} — identity known, not yet clocked in
    const [event, setEvent] = useState(null);
    const [scanOpen, setScanOpen] = useState(false);
    const [scanFor, setScanFor] = useState(null); // 'site' | 'badge'
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState({ type: "", text: "" });
    const [now, setNow] = useState(Date.now());
    const [pendingIdentity, setPendingIdentity] = useState(null); // set when a badge is scanned before a site is picked

    function selectProject(p) {
        setProject(p);
        try { localStorage.setItem(LS_PROJECT_KEY, JSON.stringify(p)); } catch {}
    }
    function changeProject() {
        setProject(null); setPersonType(null); setResolvedPerson(null);
        try { localStorage.removeItem(LS_PROJECT_KEY); } catch {}
    }

    useEffect(() => {
        if (!event) return;
        const id = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(id);
    }, [event]);

    function note(type, text) { setMsg({ type, text }); setTimeout(() => setMsg({ type: "", text: "" }), 6000); }

    async function resolveAndApply(code, requireType) {
        try {
            const { data } = await endpoints.resolveClockCode(code);
            if (requireType && data.type !== requireType) {
                note("err", "That code doesn't match what we're scanning for.");
                return;
            }
            if (data.type === "site") {
                selectProject(data.project);
                note("ok", `Site detected: ${data.project.name}`);
            } else if (data.type === "employee") {
                if (!project) {
                    // Badge scanned before a site was chosen — hold it and
                    // move to the work-confirmation step once a site is set.
                    setPendingIdentity({ id: data.employee.id, name: data.employee.employee_name });
                    note("ok", `Badge recognized: ${data.employee.employee_name}. Now select or scan the site.`);
                } else {
                    setResolvedPerson({ type: "employee", id: data.employee.id, name: data.employee.employee_name });
                }
            }
        } catch (e) { note("err", formatApiError(e)); }
    }

    // A badge was scanned first (no site yet) — the moment a site gets
    // selected, move straight to the work-confirmation step.
    useEffect(() => {
        if (project && pendingIdentity) {
            setResolvedPerson({ type: "employee", id: pendingIdentity.id, name: pendingIdentity.name });
            setPendingIdentity(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project, pendingIdentity]);

    // Landed here via a scanned poster/badge deep link (plain phone camera,
    // not our in-app scanner) — resolve it immediately on mount.
    useEffect(() => {
        if (initialCode) resolveAndApply(extractCode(initialCode));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialCode]);

    async function handleScan(text) {
        setScanOpen(false);
        await resolveAndApply(extractCode(text), scanFor === "site" ? "site" : "employee");
    }

    async function doClockIn(pType, personId, name, workDescription) {
        setBusy(true);
        try {
            const { data } = await endpoints.clockIn({ person_type: pType, person_id: personId, project_id: project.id, work_description: workDescription });
            setEvent(data.event);
            setPersonType(pType);
            setResolvedPerson(null);
            note("ok", data.already_active ? `${name || data.event.person_name} is already clocked in.` : `Clocked in — ${name || data.event.person_name}`);
        } catch (e) { note("err", formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function doClockOut(clockOutLocalValue, notes) {
        setBusy(true);
        try {
            const iso = new Date(clockOutLocalValue).toISOString();
            const { data } = await endpoints.clockOut(event.id, { clock_out_time: iso, notes });
            note("ok", `Clocked out — ${data.hours}h logged${data.cost_amount ? ` · $${data.cost_amount.toFixed(2)}` : ""}`);
            setEvent(null);
            setPersonType(null);
        } catch (e) { note("err", formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <div className="rom-card" data-testid="qr-clockin">
            <div className="rom-card-body space-y-5">
                {msg.text && (
                    <div className={`text-sm flex items-center gap-2 rounded-[6px] px-3 py-2 ${msg.type === "ok" ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`} data-testid="clockin-msg">
                        {msg.type === "ok" ? <CheckCircle size={15} weight="fill" /> : <WarningCircle size={15} weight="bold" />} {msg.text}
                    </div>
                )}

                {/* Step 1: project */}
                {!project ? (
                    <div className="space-y-3">
                        <div className="text-xs font-semibold uppercase tracking-wider text-[var(--rom-text-2)]">Step 1 · Which site?</div>
                        {pendingIdentity && (
                            <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded px-3 py-2" data-testid="pending-identity-note">
                                Badge recognized: <b>{pendingIdentity.name}</b> — pick the site below to finish clocking in.
                            </div>
                        )}
                        <button data-testid="scan-site-btn" onClick={() => { setScanFor("site"); setScanOpen(true); }} className="btn btn-primary w-full justify-center">
                            <QrCode size={16} weight="bold" /> Scan site QR
                        </button>
                        <div className="text-center text-xs text-[var(--rom-text-3)]">— or select manually —</div>
                        <select data-testid="site-fallback-select" className={inputCls} value="" onChange={(e) => {
                            const p = projects.find((x) => x.id === e.target.value);
                            if (p) selectProject({ id: p.id, name: p.name });
                        }}>
                            <option value="">Select project…</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                ) : event ? (
                    <WorkingState event={event} now={now} busy={busy} onClockOut={doClockOut} />
                ) : resolvedPerson ? (
                    <div className="space-y-4">
                        <ProjectBanner project={project} onChange={changeProject} />
                        <ConfirmWork person={resolvedPerson} busy={busy}
                            onBack={() => setResolvedPerson(null)}
                            onConfirm={(desc) => doClockIn(resolvedPerson.type, resolvedPerson.id, resolvedPerson.name, desc)} />
                    </div>
                ) : (
                    <div className="space-y-4">
                        <ProjectBanner project={project} onChange={changeProject} />
                        {!personType ? (
                            <div className="space-y-3">
                                <div className="text-xs font-semibold uppercase tracking-wider text-[var(--rom-text-2)]">Step 2 · Who are you?</div>
                                <div className="grid grid-cols-2 gap-3">
                                    <button data-testid="who-employee-btn" onClick={() => setPersonType("employee")} className="btn btn-ghost justify-center py-4 flex-col gap-1.5 border border-[var(--rom-border)]">
                                        <User size={20} weight="bold" /> Employee
                                    </button>
                                    <button data-testid="who-subcontractor-btn" onClick={() => setPersonType("subcontractor")} className="btn btn-ghost justify-center py-4 flex-col gap-1.5 border border-[var(--rom-border)]">
                                        <Truck size={20} weight="bold" /> Subcontractor
                                    </button>
                                </div>
                            </div>
                        ) : personType === "employee" ? (
                            <EmployeeIdentify employees={employees} busy={busy}
                                onBack={() => setPersonType(null)}
                                onScan={() => { setScanFor("badge"); setScanOpen(true); }}
                                onSelect={(emp) => setResolvedPerson({ type: "employee", id: emp.id, name: emp.employee_name })} />
                        ) : (
                            <SubcontractorSignIn busy={busy} onBack={() => setPersonType(null)}
                                onReady={(sub) => setResolvedPerson({ type: "subcontractor", id: sub.id, name: sub.name })} />
                        )}
                    </div>
                )}
            </div>

            <QrScannerModal open={scanOpen} onClose={() => setScanOpen(false)} onScan={handleScan}
                title={scanFor === "site" ? "Scan the site QR code" : "Scan your badge"} />
        </div>
    );
}

function ProjectBanner({ project, onChange }) {
    return (
        <div className="flex items-center justify-between gap-3 bg-[var(--rom-navy)] text-white rounded-[6px] px-3 py-2.5" data-testid="clockin-project-banner">
            <span className="text-sm font-semibold flex items-center gap-2 truncate"><MapPin size={14} weight="bold" className="shrink-0" /> {project.name}</span>
            <button onClick={onChange} data-testid="clockin-change-project" className="text-xs underline text-white/80 hover:text-white shrink-0">Change</button>
        </div>
    );
}

function EmployeeIdentify({ employees, busy, onBack, onScan, onSelect }) {
    return (
        <div className="space-y-3">
            <button onClick={onBack} className="text-xs text-[var(--rom-text-2)] hover:text-[var(--rom-text)] flex items-center gap-1"><ArrowLeft size={12} weight="bold" /> Back</button>
            <button data-testid="scan-badge-btn" disabled={busy} onClick={onScan} className="btn btn-primary w-full justify-center">
                {busy ? <CircleNotch size={16} className="animate-spin" /> : <QrCode size={16} weight="bold" />} Scan my badge
            </button>
            <div className="text-center text-xs text-[var(--rom-text-3)]">— or select manually —</div>
            <select data-testid="employee-fallback-select" disabled={busy} className={inputCls} value="" onChange={(e) => {
                const emp = employees.find((x) => x.id === e.target.value);
                if (emp) onSelect(emp);
            }}>
                <option value="">Select employee…</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.employee_name}</option>)}
            </select>
        </div>
    );
}

/* Step between identity and working: capture what work is about to be done,
 * before the clock-in is actually recorded. */
function ConfirmWork({ person, busy, onBack, onConfirm }) {
    const [desc, setDesc] = useState("");
    const [err, setErr] = useState("");

    function submit() {
        if (!desc.trim()) { setErr("Tell us what work you'll be doing before clocking in"); return; }
        setErr("");
        onConfirm(desc.trim());
    }

    return (
        <div className="space-y-3" data-testid="confirm-work-step">
            <button onClick={onBack} className="text-xs text-[var(--rom-text-2)] hover:text-[var(--rom-text)] flex items-center gap-1"><ArrowLeft size={12} weight="bold" /> Back</button>
            <div className="text-sm">Signing in as <b>{person.name}</b></div>
            <div>
                <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">What work will you be doing? *</label>
                <textarea data-testid="work-description-input" value={desc} onChange={(e) => setDesc(e.target.value)} rows={2}
                    className="w-full px-3 py-2 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none resize-none"
                    placeholder="e.g. Tear-out & debris removal, west wing" />
            </div>
            {err && <div className="text-xs text-red-600" data-testid="work-description-error">{err}</div>}
            <button data-testid="confirm-clock-in-btn" onClick={submit} disabled={busy} className="btn btn-primary w-full justify-center">
                {busy ? <CircleNotch size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />} Clock In
            </button>
        </div>
    );
}

function SubcontractorSignIn({ busy, onBack, onReady }) {
    const [email, setEmail] = useState("");
    const [checking, setChecking] = useState(false);
    const [checked, setChecked] = useState(false);
    const [found, setFound] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [form, setForm] = useState({ company_id: "", new_company: "", name: "", mobile: "", work_type: "" });
    const [err, setErr] = useState("");
    const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    useEffect(() => { endpoints.subContractorCompanies().then((r) => setCompanies(r.data || [])).catch(() => {}); }, []);

    async function checkEmail() {
        if (!email.trim()) { setErr("Enter an email to check"); return; }
        setChecking(true); setErr("");
        try {
            const { data } = await endpoints.findSubcontractorByEmail(email.trim().toLowerCase());
            setFound(data.subcontractor); setChecked(true);
            if (data.subcontractor) {
                setF("name", data.subcontractor.name); setF("mobile", data.subcontractor.mobile || "");
                setF("work_type", data.subcontractor.work_type || ""); setF("company_id", data.subcontractor.company_id || "");
            }
        } catch (e) { setErr(formatApiError(e)); }
        finally { setChecking(false); }
    }

    async function submit() {
        setErr("");
        let companyId = form.company_id;
        if (!companyId && form.new_company.trim()) {
            try {
                const { data } = await endpoints.createSubContractorCompany({ company_name: form.new_company.trim() });
                companyId = data.id;
            } catch (e) { setErr(formatApiError(e)); return; }
        }
        if (!companyId) { setErr("Select your company, or type a new one below"); return; }
        if (!form.name.trim()) { setErr("Name is required"); return; }
        try {
            const { data } = await endpoints.upsertSubcontractor({
                company_id: companyId, name: form.name.trim(), email: email.trim().toLowerCase(),
                mobile: form.mobile, work_type: form.work_type,
            });
            onReady(data);
        } catch (e) { setErr(formatApiError(e)); }
    }

    return (
        <div className="space-y-3">
            <button onClick={onBack} className="text-xs text-[var(--rom-text-2)] hover:text-[var(--rom-text)] flex items-center gap-1"><ArrowLeft size={12} weight="bold" /> Back</button>
            {!checked ? (
                <div className="flex gap-2">
                    <input data-testid="sub-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={inputCls} type="email" />
                    <button data-testid="sub-email-check" onClick={checkEmail} disabled={checking} className="btn btn-primary shrink-0">
                        {checking ? <CircleNotch size={14} className="animate-spin" /> : <MagnifyingGlass size={14} weight="bold" />}
                    </button>
                </div>
            ) : (
                <>
                    {found && <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2" data-testid="sub-matched-note">Welcome back, {found.name} — details pre-filled.</div>}
                    <Field label="Company *">
                        <select data-testid="sub-company-select" className={inputCls} value={form.company_id} onChange={(e) => setF("company_id", e.target.value)}>
                            <option value="">Select your company…</option>
                            {companies.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                        </select>
                        {!form.company_id && <div className="text-[11px] text-[var(--rom-text-3)] mt-1">Don't see your company? Type a new name below.</div>}
                    </Field>
                    {!form.company_id && (
                        <Field label="New company name">
                            <input data-testid="sub-new-company" value={form.new_company} onChange={(e) => setF("new_company", e.target.value)} className={inputCls} placeholder="e.g. Metro Skip Hire" />
                        </Field>
                    )}
                    <Field label="Your name *"><input data-testid="sub-name" value={form.name} onChange={(e) => setF("name", e.target.value)} className={inputCls} /></Field>
                    <Field label="Mobile"><input data-testid="sub-mobile" value={form.mobile} onChange={(e) => setF("mobile", e.target.value)} className={inputCls} /></Field>
                    <Field label="Work type *"><input data-testid="sub-work-type" value={form.work_type} onChange={(e) => setF("work_type", e.target.value)} className={inputCls} placeholder="e.g. Debris Removal, Trade Sub — Drywall" /></Field>
                    {err && <div className="text-xs text-red-600" data-testid="sub-signin-error">{err}</div>}
                    <button data-testid="sub-signin-submit" onClick={submit} disabled={busy} className="btn btn-primary w-full justify-center">
                        {busy ? <CircleNotch size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />} Continue
                    </button>
                </>
            )}
            {err && !checked && <div className="text-xs text-red-600" data-testid="sub-email-error">{err}</div>}
        </div>
    );
}

function WorkingState({ event, now, busy, onClockOut }) {
    const [clockOutValue, setClockOutValue] = useState(() => toLocalDatetimeValue(new Date()));
    const [notes, setNotes] = useState("");
    const [err, setErr] = useState("");

    function submit() {
        const chosen = new Date(clockOutValue);
        if (chosen <= new Date(event.clock_in)) { setErr("Clock-out time must be after your clock-in time"); return; }
        setErr("");
        onClockOut(clockOutValue, notes);
    }

    return (
        <div className="space-y-4" data-testid="clockin-working-state">
            <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-[var(--rom-green)]/15 flex items-center justify-center mx-auto mb-2">
                    <Clock size={26} weight="bold" className="text-[var(--rom-green)]" />
                </div>
                <div className="font-display font-black text-xl">{event.person_name}</div>
                <div className="text-sm text-[var(--rom-text-2)]">{event.project_name}</div>
                <div className="text-xs text-[var(--rom-text-3)] mt-1">Clocked in {elapsed(event.clock_in)} ago</div>
                {event.work_description && (
                    <div className="text-xs text-[var(--rom-text-2)] bg-slate-50 border border-[var(--rom-border)] rounded px-3 py-1.5 mt-2 inline-block" data-testid="clockin-work-description">
                        Working on: {event.work_description}
                    </div>
                )}
                {event.rate_missing && (
                    <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mt-2 inline-flex items-center gap-1.5" data-testid="rate-missing-warning">
                        <WarningCircle size={12} weight="bold" /> No rate set for "{event.work_type}" — flagged for manual reconciliation
                    </div>
                )}
            </div>
            <div>
                <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Time out (edit if you're signing out later than now)</label>
                <input data-testid="clock-out-time-input" type="datetime-local" value={clockOutValue} onChange={(e) => setClockOutValue(e.target.value)} className={inputCls} />
            </div>
            <div>
                <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Notes (optional)</label>
                <textarea data-testid="clock-out-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                    className="w-full px-3 py-2 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none resize-none" placeholder="Anything to add about the work done" />
            </div>
            {err && <div className="text-xs text-red-600" data-testid="clock-out-error">{err}</div>}
            <button data-testid="clock-out-btn" onClick={submit} disabled={busy} className="btn w-full justify-center text-white" style={{ background: "var(--rom-orange)" }}>
                {busy ? <CircleNotch size={16} className="animate-spin" /> : <SignOut size={16} weight="bold" />} Clock Out
            </button>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">{label}</label>
            {children}
        </div>
    );
}
